import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideClock, lucidePlay } from '@ng-icons/lucide';
import { BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { IS_MOBILE } from '@lite-workspace/utils';
import { PLAY_DISC } from './coach-card.component';
import { OnboardingMediaService } from './onboarding-media.service';

export interface ClipOfferContext {
  lessonId: string;
  title: string;
}

/** «Смотреть» — единственный итог; «Позже», клик мимо и Esc закрывают окно без него. */
export type ClipOfferResult = 'watch';

/**
 * Предложение посмотреть ролик вместо автозапуска: телефон — шторка снизу с вертикальным постером,
 * компьютер — окно по центру с горизонтальным. Постер и длительность — того ролика, что сыграет на этом экране.
 */
@Component({
  selector: 'onboarding-clip-offer-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, HlmButton, DialogSheetComponent, BrnDialogTitle],
  viewProviders: [provideIcons({ lucideClock, lucidePlay })],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <div class="flex flex-col items-center">
          <button
            type="button"
            class="group relative overflow-hidden rounded-[var(--r-s)] bg-stage-black"
            [class]="isMobile() ? 'h-66.75 w-37.5' : 'h-62.5 w-100'"
            aria-label="Смотреть"
            (click)="watch()"
          >
            @if (poster; as src) {
              <img [src]="src" alt="" class="absolute inset-0 size-full object-cover" />
            }
            <span aria-hidden="true" [class]="playDisc">
              <ng-icon name="lucidePlay" class="text-catalog-title text-on-media [&>svg]:fill-current" />
            </span>
          </button>

          <h2 brnDialogTitle class="mt-4.5 text-catalog-title leading-snug font-semibold text-ink">{{ context.title }}</h2>
          @if (duration) {
            <p class="mt-1 flex items-center gap-1 text-control text-c2">
              <ng-icon name="lucideClock" size="13" aria-hidden="true" />{{ duration }}
            </p>
          }

          <div class="mt-6 flex w-full gap-2.5" [class]="isMobile() ? 'flex-col' : 'flex-row-reverse'">
            <button hlmBtn type="button" size="field" [class.flex-1]="!isMobile()" (click)="watch()">Смотреть</button>
            <button hlmBtn type="button" size="field" variant="outline" [class.flex-1]="!isMobile()" (click)="later()">
              Позже
            </button>
          </div>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class ClipOfferDialogComponent {
  private readonly dialogRef = inject<BrnDialogRef<ClipOfferResult | undefined>>(BrnDialogRef);
  private readonly media = inject(OnboardingMediaService);
  protected readonly isMobile = inject(IS_MOBILE);
  protected readonly context = injectBrnDialogContext<ClipOfferContext>();

  protected readonly hostClass = `relative text-ink ${dialogSheetHostClass('m', 'p-9')}`;
  protected readonly playDisc = PLAY_DISC;
  protected readonly poster = this.media.media(this.context.lessonId)?.poster ?? null;
  protected readonly duration = this.media.duration(this.context.lessonId);

  protected watch(): void {
    this.dialogRef.close('watch');
  }

  protected later(): void {
    this.dialogRef.close(undefined);
  }
}
